import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  BiCalendar,
  BiCheck,
  BiCreditCard,
  BiEnvelope,
  BiErrorCircle,
  BiMobile,
  BiRightArrowAlt,
  BiTime,
} from "react-icons/bi";
import Footer from "../Footer/FooterPrice";
import { getUser } from "../../store/auth/storeToken";
import { hideRegisterModal, showLoginModal } from "../../store/auth/login/actions";
import { PLANS, PlanKey, formatLimit, formatPrice } from "../PricingPage/plans";
import {
  SubscriptionStatus,
  describeSubscription,
  getManagementUrl,
  refreshSubscription,
  webBillingEnabled,
} from "../../services/webBilling";

const SUPPORT_EMAIL = "support@vendororbis.com";

const planKeyOf = (tier?: string | null): PlanKey =>
  tier === "lite" ? "lite" : tier === "pro" || tier === "elite" ? "pro" : "free";

const formatDate = (date: Date | null | undefined) =>
  date
    ? date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

const STORE_LABELS: Record<string, string> = {
  rc_billing: "Card, on vendororbis.com",
  stripe: "Card, on vendororbis.com",
  app_store: "App Store",
  mac_app_store: "App Store",
  play_store: "Google Play",
  promotional: "Complimentary",
};

type Pill = { label: string; tone: "green" | "blue" | "amber" | "slate" };

const PILL_STYLES: Record<Pill["tone"], string> = {
  green: "bg-emerald-400/15 text-emerald-300 ring-emerald-400/30",
  blue: "bg-blue-400/15 text-blue-300 ring-blue-400/30",
  amber: "bg-amber-400/15 text-amber-300 ring-amber-400/30",
  slate: "bg-white/10 text-slate-300 ring-white/15",
};

const statusPill = (paid: boolean, status: SubscriptionStatus | null): Pill => {
  if (!paid) return { label: "Free forever", tone: "slate" };
  if (status?.billingIssue) return { label: "Payment issue", tone: "amber" };
  if (status && !status.willRenew) return { label: "Cancelled", tone: "amber" };
  if (status?.trial) return { label: "Free trial", tone: "blue" };
  return { label: "Active", tone: "green" };
};

const Detail = ({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof BiCalendar;
  label: string;
  value: string;
}) => (
  <div className="flex gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
    <Icon size={20} className="mt-0.5 flex-shrink-0 text-blue-400" />
    <div>
      <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-semibold text-white">{value}</p>
    </div>
  </div>
);

const Billing = () => {
  const dispatch = useDispatch();
  const [user, setUserState] = useState(() => getUser());
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const planKey = planKeyOf(status?.tier ?? user?.tier);
  const plan = PLANS[planKey];
  const paid = planKey !== "free" && (!!status || !!user?.isPaid);

  // Pick up a purchase or cancellation made elsewhere (app, web portal).
  useEffect(() => {
    if (!webBillingEnabled || !user?._id) return;
    setRefreshing(true);
    refreshSubscription(user._id)
      .then((info) => {
        setStatus(describeSubscription(info));
        setUserState(getUser());
      })
      .catch((e) => console.error("Couldn't refresh subscription", e))
      .finally(() => setRefreshing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  const openManagement = async () => {
    if (!webBillingEnabled || !user?._id) {
      window.alert(
        "Please manage your subscription in the mobile app. Purchases and changes are handled through iOS or Android.",
      );
      return;
    }
    setOpening(true);
    setError(null);
    try {
      const url = await getManagementUrl(user._id);
      if (url) {
        window.location.href = url;
      } else {
        setError("This plan can't be managed online. Email us and we'll sort it out.");
      }
    } catch (e) {
      console.error("Couldn't open subscription management", e);
      setError("Couldn't open subscription management. Please try again.");
    } finally {
      setOpening(false);
    }
  };

  const inApp = status?.store === "app_store" || status?.store === "play_store";
  const yearly = user?.subscriptionInterval === "yearly";
  const price = !paid
    ? "$0"
    : yearly
      ? `${formatPrice(inApp ? plan.appAnnualPrice : plan.webAnnualPrice)}/year`
      : `${formatPrice(plan.monthlyPrice)}/month`;

  const endDate = formatDate(status?.expiresAt) ?? "—";
  const dateDetail =
    !paid || !status
      ? null
      : status.trial && status.willRenew
        ? { icon: BiTime, label: "Trial ends", value: endDate }
        : !status.willRenew
          ? { icon: BiCalendar, label: "Access until", value: endDate }
          : { icon: BiCalendar, label: "Renews on", value: endDate };

  const pill = statusPill(paid, status);
  const upgrades = (["lite", "pro"] as const).filter(
    (key) => !paid || (planKey === "lite" && key === "pro"),
  );

  return (
    <>
      <section className="relative isolate overflow-hidden bg-[#070b17] px-4 pb-20 pt-28 text-white md:px-8 md:pt-32">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(rgba(148,163,184,0.18)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_70%_55%_at_50%_0%,black,transparent)]"
        />
        <div
          aria-hidden
          className="absolute -top-40 left-1/2 -z-10 h-[480px] w-[760px] -translate-x-1/2 rounded-full bg-blue-600/25 blur-[120px]"
        />

        <div className="mx-auto max-w-4xl">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-400">
            Billing
          </p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight md:text-5xl">Your plan</h1>
          <p className="mt-3 max-w-xl text-slate-400 md:text-lg">
            See what you're on, when it renews, and change or cancel any time.
          </p>

          {!user?._id ? (
            <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center">
              <p className="text-lg font-semibold">Log in to see your plan</p>
              <p className="mt-2 text-sm text-slate-400">
                Your plan works the same on the website, iPhone, and Android.
              </p>
              <button
                type="button"
                onClick={() => {
                  dispatch(hideRegisterModal());
                  dispatch(showLoginModal());
                }}
                className="mt-6 rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 hover:brightness-110"
              >
                Log in
              </button>
            </div>
          ) : (
            <>
              <div
                className={`mt-10 rounded-3xl p-px ${
                  paid
                    ? "bg-gradient-to-br from-blue-500/70 via-violet-500/50 to-white/10 shadow-[0_0_90px_-30px_rgba(99,102,241,0.8)]"
                    : "bg-white/10"
                }`}
              >
                <div className="rounded-[23px] bg-[#0d1528] p-6 md:p-8">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-2xl font-bold">{plan.title}</h2>
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${PILL_STYLES[pill.tone]}`}
                        >
                          {pill.label}
                        </span>
                        {refreshing && (
                          <span className="text-xs text-slate-500">Checking…</span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-slate-400">{plan.tagline}</p>
                    </div>
                    <p className="text-3xl font-bold tracking-tight">{price}</p>
                  </div>

                  {status?.billingIssue && (
                    <div className="mt-6 flex gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-200">
                      <BiErrorCircle size={20} className="flex-shrink-0" />
                      We couldn't charge your card. Update your payment method to keep{" "}
                      {plan.title}.
                    </div>
                  )}

                  <div className="mt-6 grid gap-3 sm:grid-cols-2">
                    {dateDetail && <Detail {...dateDetail} />}
                    {paid && status && (
                      <Detail
                        icon={inApp ? BiMobile : BiCreditCard}
                        label="Billed through"
                        value={STORE_LABELS[status.store] ?? "The VendorOrbis app"}
                      />
                    )}
                    <Detail
                      icon={BiCheck}
                      label="Events"
                      value={`${formatLimit(plan.limits.events)} active`}
                    />
                    <Detail
                      icon={BiCheck}
                      label="Inventory"
                      value={`${formatLimit(plan.limits.items)} items`}
                    />
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    {paid && status?.store === "promotional" ? null : paid ? (
                      <button
                        type="button"
                        onClick={openManagement}
                        disabled={opening}
                        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition hover:brightness-110 disabled:opacity-60"
                      >
                        {opening
                          ? "Opening…"
                          : inApp
                            ? `Manage in ${STORE_LABELS[status!.store]}`
                            : "Manage subscription"}
                        <BiRightArrowAlt size={18} />
                      </button>
                    ) : (
                      <Link
                        to="/pricing"
                        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition hover:brightness-110"
                      >
                        Try Pro free for 30 days
                        <BiRightArrowAlt size={18} />
                      </Link>
                    )}
                    <Link
                      to="/pricing"
                      className="rounded-xl border border-white/10 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
                    >
                      Compare plans
                    </Link>
                  </div>
                  {paid && status && (
                    <p className="mt-3 text-sm text-slate-400">
                      {inApp
                        ? `You subscribed in ${STORE_LABELS[status.store]}, so changes and cancelling happen there.`
                        : status.store === "promotional"
                          ? "Your plan was added to your account, so there's nothing to manage."
                          : "You subscribed here on the website. Manage it here or from the link in your receipt email."}
                    </p>
                  )}
                  {error && <p className="mt-3 text-sm text-red-400">{error}</p>}

                  <div className="my-8 h-px bg-white/10" />

                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    {plan.featuresHeading}
                  </p>
                  <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex gap-3 text-sm text-slate-300">
                        <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-blue-400">
                          <BiCheck size={14} />
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {upgrades.length > 0 && (
                <div className="mt-12">
                  <h3 className="text-xl font-semibold">
                    {paid ? "Need more?" : "Outgrowing Free?"}
                  </h3>
                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    {upgrades.map((key) => {
                      const upgrade = PLANS[key];
                      return (
                        <Link
                          key={key}
                          to="/pricing"
                          className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition hover:-translate-y-0.5 hover:border-white/20"
                        >
                          <div className="flex items-baseline justify-between gap-3">
                            <p className="text-lg font-semibold">{upgrade.title}</p>
                            <p className="text-slate-300">
                              <span className="text-2xl font-bold text-white">
                                {formatPrice(upgrade.monthlyPrice)}
                              </span>
                              /month
                            </p>
                          </div>
                          <p className="mt-1 text-sm text-slate-400">{upgrade.tagline}</p>
                          <p className="mt-4 text-sm text-slate-300">
                            {formatLimit(upgrade.limits.events)} events ·{" "}
                            {formatLimit(upgrade.limits.items)} items
                          </p>
                          <p className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-400 group-hover:text-blue-300">
                            {key === "pro" ? "Start 30-day free trial" : "See Lite"}
                            <BiRightArrowAlt size={18} />
                          </p>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="mt-12 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <BiCalendar size={22} className="text-blue-400" />
              <p className="mt-3 font-semibold">Cancel any time</p>
              <p className="mt-1 text-sm text-slate-400">
                You keep your plan until the end of the period you paid for. Your data stays
                put.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <BiMobile size={22} className="text-blue-400" />
              <p className="mt-3 font-semibold">One plan, every device</p>
              <p className="mt-1 text-sm text-slate-400">
                Your plan unlocks the same features on the website, iPhone, and Android.
              </p>
            </div>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition hover:border-white/20"
            >
              <BiEnvelope size={22} className="text-blue-400" />
              <p className="mt-3 font-semibold">Questions about billing?</p>
              <p className="mt-1 text-sm text-slate-400">{SUPPORT_EMAIL}</p>
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default Billing;
